import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { Warehouse } from '../types/warehouse';
import { warehouseService } from '../services/warehouseService';

interface WarehouseContextType {
  currentWarehouse: string | null;
  currentWarehouseName: string | null;
  warehouses: Warehouse[];
  isLoading: boolean;
}

const WarehouseContext = createContext<WarehouseContextType | null>(null);

interface WarehouseProviderProps {
  children: ReactNode;
}

export const WarehouseProvider: React.FC<WarehouseProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [currentWarehouse, setCurrentWarehouse] = useState<string | null>(null);
  const [currentWarehouseName, setCurrentWarehouseName] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load current warehouse from localStorage when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const stored = localStorage.getItem('bizclick_warehouse');
      setCurrentWarehouse(stored || null);
    } else {
      setCurrentWarehouse(null);
      setCurrentWarehouseName(null);
      setWarehouses([]);
    }
  }, [isAuthenticated, user]);

  // Fetch warehouse list and resolve name
  useEffect(() => {
    if (!isAuthenticated || !currentWarehouse) {
      return;
    }

    let cancelled = false;

    const fetchWarehouses = async () => {
      setIsLoading(true);
      try {
        const list = await warehouseService.getWarehouses();
        if (cancelled) return;

        setWarehouses(list);

        const match = list.find(w => w.warehouseID === currentWarehouse);
        setCurrentWarehouseName(match?.warehouseDescr || currentWarehouse);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch warehouses:', error);
        setCurrentWarehouseName(currentWarehouse);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchWarehouses();

    return () => { cancelled = true; };
  }, [isAuthenticated, currentWarehouse]);

  return (
    <WarehouseContext.Provider value={{ currentWarehouse, currentWarehouseName, warehouses, isLoading }}>
      {children}
    </WarehouseContext.Provider>
  );
};

export const useWarehouse = () => {
  const context = useContext(WarehouseContext);
  if (!context) throw new Error('useWarehouse must be used within WarehouseProvider');
  return context;
};
